import {
  ActionCommand,
  NavigationCommand,
  getDurationinMinutes,
} from '@nextforce/api'
import { HeaderBackButton } from '@react-navigation/elements'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCreateMessage } from 'chat/messages/queries/create'
import {
  ConfirmShiftActionScreenProps,
  HomeStackParamList,
} from 'home/stack/types'
import {
  default as React,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'
import { useScheduleContext } from 'schedule/schedule_context'
import { confirmationAlert } from 'shared/alerts'
import { showHeaderTitle } from 'shared/header/header_title'
import { showStoreReview } from 'shared/store_review'
import { usePerformShiftAction } from 'shifts/queries/perform_action'
import ConfirmShiftActionView from 'shifts/screens/confirm_shift_action/view'
import { useShiftActions } from 'shifts/shift_actions_context'
import { getEndDateTime, getStartDateTime } from 'shifts/utils'
import { DEFAULT_STORE_PREFERENCES } from 'stores/constants'
import { useRecentStoresContext } from 'stores/context'
import { useStores } from 'stores/queries/list'

export function ConfirmShiftActionScreen({
  navigation,
  route,
}: ConfirmShiftActionScreenProps) {
  const shiftMutation = usePerformShiftAction()
  const createMessageMutation = useCreateMessage()
  const { handleNavigation } = useRoutingContext()
  const { setSchedule } = useScheduleContext()
  const { shiftData } = route.params
  const { setActionCommand } = useShiftActions()

  const { data: stores } = useStores(data => data.stores)
  const { activeStoreId } = useRecentStoresContext()
  const activeStore = stores?.find(s => s.id === activeStoreId)

  const preferences = activeStore?.preferences ?? DEFAULT_STORE_PREFERENCES
  const [customMessage, setCustomMessage] = useState<string | undefined>()

  const navigateToSchedule = useCallback(() => {
    handleNavigation({ routeName: 'schedule', resetNavigationState: true })
  }, [handleNavigation])

  const navigateToMyShifts = useCallback(() => {
    setSchedule({ calendar: 'helper' })
    handleNavigation({ routeName: 'schedule' })
  }, [handleNavigation, setSchedule])

  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: props => (
        <HeaderBackButton {...(props as any)} onPress={navigateToSchedule} />
      ),
    })
  }, [navigateToSchedule, navigation])

  useLayoutEffect(() => {
    if (shiftData.title) {
      showHeaderTitle(navigation, shiftData.title)
    }
  }, [navigation, shiftData?.title])

  useEffect(() => {
    if (
      shiftData.status === 'CLOSED' &&
      (shiftData.type === 'COVER' || shiftData.type === 'FILL')
    ) {
      showStoreReview()
    }
  }, [shiftData.status, shiftData.title, shiftData.type])

  const onCommandButtonPress = useCallback(
    async (command: ActionCommand | NavigationCommand) => {
      let confirmed = true

      if (command.type === 'navigate') {
        if (command.route.name === 'open_shifts') {
          setActionCommand(undefined)
          handleNavigation({
            routeName: 'open_shifts',
            resetNavigationState: true,
          })
        } else if (command.route.name === 'schedule') {
          setActionCommand(undefined)
          navigateToSchedule()
        } else {
          handleNavigation({
            routeName: command.route.name as keyof HomeStackParamList,
            routeParams: command.route.params && { ...command.route.params },
          })
        }
      } else if (command.type === 'action') {
        if (command.name === 'store_chat') {
          const defaultMessage: string = command.action.params?.body
          const shiftUri = command.action.params?.deeplink
          const message = customMessage
            ? customMessage.concat('\n', shiftUri)
            : defaultMessage
          await createMessageMutation.mutateAsync({
            uri: command.action.uri,
            content: {
              body: message,
            },
          })
          navigateToMyShifts()
        }
        if (
          command.name === 'create_shift' &&
          preferences.nextShiftSplitterEnabled &&
          !!shiftData.shift &&
          getDurationinMinutes(
            getStartDateTime(shiftData.shift.date, shiftData.shift.startTime),
            getEndDateTime(
              shiftData.shift.date,
              shiftData.shift.startTime,
              shiftData.shift.endTime
            )
          ) > preferences.nextShiftSplitterLength
        ) {
          confirmed = await confirmationAlert(
            t('splitNextShiftFromCancelledShiftTitle'),
            t('splitNextShiftFromCancelledShiftMessage')
          )
        }
        if (confirmed) {
          const res = await shiftMutation.mutateAsync(command.action)

          if (res) {
            navigation.setParams({ shiftData: res })
          }
        }
      }
    },
    [
      createMessageMutation,
      customMessage,
      handleNavigation,
      navigateToMyShifts,
      navigateToSchedule,
      navigation,
      preferences.nextShiftSplitterEnabled,
      preferences.nextShiftSplitterLength,
      setActionCommand,
      shiftData.shift,
      shiftMutation,
    ]
  )

  return (
    <ConfirmShiftActionView
      {...{
        shiftData,
        onCommandButtonPress,
        customMessage,
        setCustomMessage,
      }}
    />
  )
}
